import { useState } from 'react'
import Select from 'react-select'
import { FiCheckCircle, FiRotateCcw, FiPaperclip } from 'react-icons/fi'

import api from '../../utils/api'
import Input from '../../shared/components/form/input'
import Button from '../../shared/components/Button/Button'
import selectStyles from '../../shared/css/SelectStyle'
import { FINANCE_PAYMENT_METHODS, FINANCE_STATUS_LABEL } from '../../shared/constants/financePaymentMethods'
import TimestampToString from '../../shared/functions/TimestampToString'
import useFlashMessage from '../../hooks/useFlashMessage'
import serverURL from '../../utils/serverURL'

import './Finance.css'

const today = () => new Date().toISOString().substring(0, 10)

// Detalhe de parcelas de um lançamento — dar baixa (com juros/multa/desconto, forma de
// pagamento e anexo de comprovante) ou desfazer uma baixa feita por engano.
function ParcelsModal({ entry, myPermissionEdit, handleOnSuccess }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()
    const { timeStampToDDMMAAAA } = TimestampToString()

    const [payingNumber, setPayingNumber] = useState(null)
    const [paidAt, setPaidAt] = useState(today())
    const [paidValue, setPaidValue] = useState('')
    const [interest, setInterest] = useState('0')
    const [fine, setFine] = useState('0')
    const [discount, setDiscount] = useState('0')
    const [paymentMethod, setPaymentMethod] = useState(null)
    const [obs, setObs] = useState('')
    const [attachment, setAttachment] = useState(null)

    function openPay(parcel) {
        setPayingNumber(parcel.number)
        setPaidAt(today())
        setPaidValue(String(parcel.value))
        setInterest('0')
        setFine('0')
        setDiscount('0')
        setPaymentMethod(null)
        setObs('')
        setAttachment(null)
    }

    async function submitPay() {
        const formData = new FormData()
        formData.append('paidAt', paidAt)
        formData.append('paidValue', paidValue)
        formData.append('interest', interest)
        formData.append('fine', fine)
        formData.append('discount', discount)
        if (paymentMethod) formData.append('paymentMethod', paymentMethod.value)
        formData.append('obs', obs)
        if (attachment) formData.append('attachment', attachment)

        let msgType = 'success'
        const data = await api.patch(`/finance/entry/${entry._id}/parcel/${payingNumber}/pay`, formData, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setPayingNumber(null)
            handleOnSuccess()
        }
    }

    async function undoPay(number) {
        let msgType = 'success'
        const data = await api.patch(`/finance/entry/${entry._id}/parcel/${number}/undo`, {}, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') handleOnSuccess()
    }

    return (
        <div className="fin-parcels">
            <p className="fin-parcels-title">Parcelas — {entry.description}</p>
            <span className="fin-parcels-title-accent" />

            {entry.parcels.map((p) => (
                <div key={p.number} className="fin-parcel-row">
                    <div className="fin-parcel-info">
                        <span className="fin-parcel-number">
                            Parcela {p.number}/{entry.parcels.length} — R$ {p.value.toFixed(2)}
                            {' '}<span className={`fin-status fin-status-${p.status}`}>{FINANCE_STATUS_LABEL[p.status]}</span>
                        </span>
                        <span className="fin-parcel-detail">Vencimento: {timeStampToDDMMAAAA(new Date(p.dueDate).toISOString())}</span>
                        {p.status === 'pago' && (
                            <span className="fin-parcel-detail">
                                Pago em {timeStampToDDMMAAAA(new Date(p.paidAt).toISOString())} — R$ {p.paidValue?.toFixed(2)}
                                {p.paymentMethod && ` · ${FINANCE_PAYMENT_METHODS.find((m) => m.value === p.paymentMethod)?.label || p.paymentMethod}`}
                                {(p.interest > 0 || p.fine > 0) && ` · juros/multa: R$ ${(p.interest + p.fine).toFixed(2)}`}
                                {p.discount > 0 && ` · desconto: R$ ${p.discount.toFixed(2)}`}
                            </span>
                        )}
                        {p.attachment?.url && (
                            <a className="fin-parcel-attachment" href={`${serverURL}${p.attachment.url}`} target="_blank" rel="noreferrer">
                                <FiPaperclip size={11} /> {p.attachment.filename}
                            </a>
                        )}
                        {p.obs && <span className="fin-parcel-detail">Obs: {p.obs}</span>}
                    </div>
                    {myPermissionEdit && (
                        <div className="fin-parcel-actions">
                            {p.status === 'aberto' && (
                                <FiCheckCircle className="shd-icon-row-edit" title="Dar baixa" onClick={() => openPay(p)} />
                            )}
                            {p.status === 'pago' && (
                                <FiRotateCcw className="shd-icon-row-edit" title="Desfazer baixa" onClick={() => undoPay(p.number)} />
                            )}
                        </div>
                    )}
                </div>
            ))}

            {payingNumber !== null && (
                <div className="fin-pay-form">
                    <p className="fin-parcels-title" style={{ fontSize: '0.95rem' }}>Dar baixa na parcela {payingNumber}</p>

                    <div className="fl-row">
                        <div className="form-group">
                            <Input text="Data do pagamento" type="date" name="paidAt" handleOnChange={(e) => setPaidAt(e.target.value)} value={paidAt} />
                        </div>
                        <div className="form-group">
                            <Input text="Valor pago (R$)" type="number" name="paidValue" placeholder="0.00" handleOnChange={(e) => setPaidValue(e.target.value)} value={paidValue} />
                        </div>
                        <div className="form-group">
                            <label className="input-label">Forma de pagamento:</label>
                            <Select
                                options={FINANCE_PAYMENT_METHODS}
                                styles={selectStyles}
                                value={paymentMethod}
                                onChange={setPaymentMethod}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                placeholder="Selecione..."
                            />
                        </div>
                    </div>

                    <div className="fl-row">
                        <div className="form-group">
                            <Input text="Juros (R$)" type="number" name="interest" placeholder="0.00" handleOnChange={(e) => setInterest(e.target.value)} value={interest} />
                        </div>
                        <div className="form-group">
                            <Input text="Multa (R$)" type="number" name="fine" placeholder="0.00" handleOnChange={(e) => setFine(e.target.value)} value={fine} />
                        </div>
                        <div className="form-group">
                            <Input text="Desconto (R$)" type="number" name="discount" placeholder="0.00" handleOnChange={(e) => setDiscount(e.target.value)} value={discount} />
                        </div>
                    </div>

                    <div className="form-group">
                        <Input text="Observação" type="text" name="obs" placeholder="Opcional" handleOnChange={(e) => setObs(e.target.value)} value={obs} />
                    </div>

                    <div className="form-group">
                        <label className="input-label">Comprovante (opcional, PDF/JPG/PNG):</label>
                        <input className="input" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setAttachment(e.target.files[0])} />
                    </div>

                    <div className="prod-send-actions">
                        <Button variant="cancel" onClick={() => setPayingNumber(null)}>Cancelar</Button>
                        <Button variant="save" onClick={submitPay}>Confirmar Baixa</Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ParcelsModal
